/**
 * The record list. Table markup follows
 * pages/UserFeedback/components/FeedbackSubmissionsTable.tsx.
 *
 * There is no email column and there never will be one: the record holds no email, and
 * a completed deletion removes user_info, so the uid resolves to nobody. The uid plus
 * UserLinks is the whole identity surface.
 */
import EmptyState from '../../../components/EmptyState';
import { UserLinks } from '../../../components/UserLinks';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import type { AccountDeletionRecord } from '../types/accountDeletionTypes';
import {
  isInconsistent,
  isStalled,
  needsAttention,
  sweepSummary,
} from '../utils/deletionFilters';
import {
  AttentionBadge,
  DeletionStatusPill,
  InconsistentBadge,
  PostHogOutcomeBadge,
  RevenueCatOutcomeBadge,
  SourceBadge,
  StalledBadge,
} from './DeletionBadges';

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD = 'px-4 py-3 whitespace-nowrap text-sm text-gray-900';

/**
 * Documents first, locations second. "7 locations" is not "7 documents", and showing
 * only the location count made an empty sweep look like a busy one.
 */
const SweepCell = ({ record }: { record: AccountDeletionRecord }) => {
  const summary = sweepSummary(record);

  if (summary.notReached) {
    return (
      <span className="text-xs text-amber-700" title="The Firestore sweep never ran for this record">
        Not reached
      </span>
    );
  }

  const locationsRun = summary.locationsDeleted + summary.locationsFailed;

  return (
    <span
      className="text-xs text-gray-600"
      title={`${summary.documentsDeleted} document(s) removed across ${locationsRun} location(s). Nothing to delete is the normal result for a user with no data left.`}
    >
      <span className={summary.documentsDeleted === 0 ? 'text-gray-400' : 'font-medium text-gray-800'}>
        {summary.documentsDeleted} doc{summary.documentsDeleted === 1 ? '' : 's'}
      </span>
      <span className="text-gray-400"> · {locationsRun} loc</span>
      {summary.locationsFailed > 0 && (
        <span className="text-red-600 font-medium"> · {summary.locationsFailed} failed</span>
      )}
    </span>
  );
};

interface AccountDeletionsTableProps {
  records: AccountDeletionRecord[];
  onOpen: (record: AccountDeletionRecord) => void;
}

export const AccountDeletionsTable = ({ records, onOpen }: AccountDeletionsTableProps) => {
  const { formatDate } = useFormatUtils();

  if (records.length === 0) {
    return (
      <EmptyState
        title="No deletion records"
        description="No account_deletions document matches these filters."
      />
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Status</th>
              <th className={TH}>User ID</th>
              <th className={TH}>Source</th>
              <th className={TH}>Requested</th>
              <th className={TH}>Updated</th>
              <th className={TH}>Completed</th>
              <th className={TH}>Attempts</th>
              <th className={TH}>RevenueCat</th>
              <th className={TH}>PostHog</th>
              <th className={TH}>Firestore sweep</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {records.map((record) => (
              <tr
                key={record.uid}
                onClick={() => onOpen(record)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className={TD}>
                  <div className="flex flex-wrap items-center gap-1">
                    <DeletionStatusPill status={record.status} />
                    {needsAttention(record) && <AttentionBadge />}
                    {isStalled(record) && <StalledBadge />}
                    {isInconsistent(record) && <InconsistentBadge />}
                  </div>
                </td>
                <td className={TD}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-600">{record.uid}</span>
                    <UserLinks userId={record.uid} mode="compact" />
                  </div>
                </td>
                <td className={TD}>
                  <SourceBadge source={record.source} />
                </td>
                <td className={`${TD} text-gray-500 text-xs`}>{formatDate(record.requestedAt)}</td>
                <td className={`${TD} text-gray-500 text-xs`}>{formatDate(record.updatedAt)}</td>
                <td className={`${TD} text-gray-500 text-xs`}>{formatDate(record.completedAt)}</td>
                <td className={TD}>
                  <span
                    className={
                      needsAttention(record) ? 'font-semibold text-red-600' : 'text-gray-600'
                    }
                  >
                    {record.attemptCount}
                  </span>
                </td>
                <td className={TD}>
                  <RevenueCatOutcomeBadge audit={record.revenueCat} />
                </td>
                <td className={TD}>
                  <PostHogOutcomeBadge audit={record.posthog} completed={record.status === 'completed'} />
                </td>
                <td className={TD}>
                  <SweepCell record={record} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
